import { css } from '@emotion/react';
import {
  Accordion,
  BREAKPOINTS,
  Checkbox,
  Divider,
  Link,
  Modal,
  Paragraph,
  semanticTokens,
  SvgIcon,
  tokens,
  useDialog,
} from '@flysas-tech/commercial-ui';
import { t, Trans } from 'i18n';
import { LegacyRef } from 'react';

import { useMediaQuery } from '../../hooks';
import { Card } from '../Card';

const styles = {
  card: css`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: ${tokens.dimension['space-2']};

    @container main-container (min-width: ${BREAKPOINTS.layout.sm}px) {
      flex-direction: row-reverse;
      align-items: flex-start;
      gap: ${tokens.dimension['space-6']};
    }
  `,
  iconContainer: css`
    display: inline-grid;
    grid-template-columns: repeat(4, 1fr);
    gap: ${tokens.dimension['space-1']};
    padding: ${tokens.dimension['space-1']};
    background-color: ${tokens.color.turquoise[90]};

    @container main-container (min-width: ${BREAKPOINTS.layout.sm}px) {
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(2, 1fr);
      padding: ${tokens.dimension['space-4']};
      align-self: center;
    }
  `,
  icon: css`
    background-color: ${tokens.color.turquoise[80]};
  `,
  information: css`
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-3']};

    & input,
    & label {
      cursor: pointer;
    }
  `,
  button: css`
    all: unset;
    cursor: pointer;
    text-decoration: underline;
    color: ${semanticTokens.color.text.link};

    &:focus-visible {
      outline: 2px solid ${semanticTokens.color.border.focus};
      border-radius: ${tokens.cornerRadius.small};
    }
  `,
  prominent: css`
    font-style: italic;
  `,
  modalContainer: css`
    & > div {
      width: 100%;
    }
    & h3 {
      text-align: left;
    }
  `,
};

type Props = {
  assistanceActive: boolean;
  onChange: () => void;
};

export const RequestAssistance = ({ onChange, assistanceActive }: Props) => {
  const { dialogProps, toggleDialog } = useDialog({ modal: true });
  const isLargerThanMobile = useMediaQuery({ min: BREAKPOINTS.layout.sm });

  return (
    <>
      <Card css={styles.card}>
        <div css={styles.iconContainer}>
          <SvgIcon icon="Wheelchair" size={isLargerThanMobile ? 'large' : 'medium'} css={styles.icon} />
          <SvgIcon icon="DisabilitySight" size={isLargerThanMobile ? 'large' : 'medium'} css={styles.icon} />
          <SvgIcon icon="DisabilityHearing" size={isLargerThanMobile ? 'large' : 'medium'} css={styles.icon} />
          <SvgIcon icon="Mobility" size={isLargerThanMobile ? 'large' : 'medium'} css={styles.icon} />
        </div>
        <div css={styles.information}>
          <div>
            <Checkbox
              label={t('assistance.addForJourney')}
              onChange={onChange}
              checked={assistanceActive}
              data-testid="add-assistance"
            />
            <Divider direction={'horizontal'} variant={'neutral'} />
          </div>
          <Paragraph color="primary" size="paragraph-2">
            <Trans
              i18nKey="selectOptionsForAssistanceText"
              components={{
                button: <button css={styles.button} onClick={() => toggleDialog()} />,
              }}
              defaults={`You can select options based on each traveler’s needs, for example assistance with mobility or hearing or
              visual impairments. This also includes assistance in the event of injury or illness. Read more about assistance.`}
            />
          </Paragraph>
          <Paragraph css={styles.prominent} color="primary" size="paragraph-2">{`${t('mmb:assistance.generalInfo', {
            defaultValue:
              'These services are free, subject to availability and must be requested at least 48 hours before departure.',
          })}`}</Paragraph>
        </div>
      </Card>

      <Modal.Root ref={dialogProps.ref as LegacyRef<HTMLDialogElement>} onClose={() => toggleDialog()}>
        <Modal.Container css={styles.modalContainer}>
          <Modal.Title>{t('assistance.modal.readMore.title')}</Modal.Title>
          <Accordion
            type="multiple"
            headingElement="h3"
            items={[
              {
                heading: t('assistance.modal.readMore.q1'),
                content: t('assistance.modal.readMore.a1'),
                key: 'who-can-request',
              },
              {
                heading: t('assistance.modal.readMore.q2'),
                content: t('assistance.modal.readMore.a2'),
                key: 'what-happens-after',
              },
              {
                heading: t('assistance.modal.readMore.q3'),
                content: t('assistance.modal.readMore.a3'),
                key: 'help-connection-flights',
              },
              {
                heading: t('assistance.modal.readMore.q4'),
                content: (
                  <Trans
                    i18nKey="assistance.modal.readMore.a4"
                    defaultValue="If you need assistance for an intellectual impairment, you'll need to fill out a form to request assistance."
                    components={{
                      anchorLink: (
                        <Link
                          href={t('assistance.needsRequestForm.link')}
                          size="link-2"
                          styleAs="link"
                          target="_blank"
                        />
                      ),
                    }}
                  />
                ),
                key: 'intellectual-impairment',
              },
            ]}
          />
          <Paragraph color="primary" size="paragraph-2">
            <Trans
              i18nKey="assistance.modal.moreInfo.text"
              defaultValue={`Find out more about assistance`}
              components={{
                anchorLink: <Link href={t('assistance.moreInfo.link')} size="link-2" styleAs="link" target="_blank" />,
              }}
            />
          </Paragraph>
        </Modal.Container>
      </Modal.Root>
    </>
  );
};
